import {
  Attachment,
  Close,
  FolderOutlined,
  PersonOutline,
  PushPin,
  RemoveCircleOutline,
} from '@mui/icons-material';
import {
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  styled,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import DebouncedSearchInput from '../../components/DebouncedSearchInput';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import {
  getFolderFiles,
  getUserFiles,
  postFile,
  postFilePrivate,
} from '../../redux/actions/fileActions';
import { myFolder } from '../../redux/consts';
import { selectFiles, setSearchQuery } from '../../redux/reducers/fileReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { CustomFile } from '../../Types/file';
import { formatDate } from '../../util/dateUtils';
import AutoTruncatingTypographyWithTooltip from '../AutoTruncatingTypographyWithTooltip';
import LoadingMask from '../LoadingMask';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';

const DialogContainer = styled(Container)(({ theme }) => ({
  marginTop: '8px',
  marginBottom: '24px',
  padding: '0px 0px !important',

  [theme.breakpoints.up('sm')]: {
    padding: '0px',
  },
}));

const FilesSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  margin: '0 0px 20px 0px',
});

const SearchContainer = styled('div')({
  margin: '10px 15px 0px 15px',
});

export type FileUploadDialogProps = {
  userFiles: CustomFile[];
  folderFiles: CustomFile[];
  currentlySelectedFiles?: CustomFile[];
  setShouldRefetchFiles: (shouldRefetch: boolean) => void;
  setFilesToUpload?: Dispatch<SetStateAction<File[]>>;
  openDialog: boolean;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  buttonConfirmText: string;
  handleCloseDialog: () => void;
  handleConfirmDialog?: (selectedFiles: CustomFile[]) => void;
  showSearch?: boolean;
  defaultSaveLocationIsUser?: boolean;
  isTemporary?: boolean;
  folderFilesOnly?: boolean;
};

const FileUploadDialog = ({
  userFiles,
  folderFiles,
  currentlySelectedFiles,
  setShouldRefetchFiles,
  setFilesToUpload,
  openDialog,
  isLoading,
  setIsLoading,
  buttonConfirmText,
  handleCloseDialog,
  handleConfirmDialog,
  showSearch = false,
  defaultSaveLocationIsUser = true,
  isTemporary = false,
  folderFilesOnly = false,
}: FileUploadDialogProps) => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { searchQuery } = useSelector(selectFiles);
  const { currentFolder } = useSelector(selectFolders);
  const [filesToProcess, setFilesToProcess] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<CustomFile[] | undefined>(
    currentlySelectedFiles
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [filesForFolder, setFilesForFolder] = useState<boolean>(
    !defaultSaveLocationIsUser || folderFilesOnly
  );

  useEffect(() => {
    setFilesForFolder(!defaultSaveLocationIsUser || folderFilesOnly);
  }, [defaultSaveLocationIsUser, folderFilesOnly]);

  const processFiles = async (passedFilesToProcess?: File[]) => {
    setIsProcessing(true);
    setIsLoading(true);
    const filesJustProcessed: CustomFile[] = [];

    const actualFilesToProcess = passedFilesToProcess ?? filesToProcess;

    const uploadPromises = actualFilesToProcess.map((file) => {
      const fileUploadAnalyticsMetadata = {
        value: file.name,
        size: file.size.toString(),
        type: file.type,
      };

      return (async () => {
        const formData = new FormData();
        formData.append('file', file);
        try {
          // Use postFilePrivate for temporary chat, otherwise use regular postFile
          let action;
          if (isTemporary) {
            action = await dispatch(postFilePrivate(formData));
          } else {
            action = await dispatch(
              postFile({ formData, folderId: filesForFolder ? currentFolder?.id : undefined })
            );
          }
          const messageFile = action.payload as CustomFile;
          analytics.event(AnalyticsEvent.WM_NIGEL_FILE_UPLOAD, fileUploadAnalyticsMetadata, {
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          });
          if (messageFile.statusText) {
            enqueueSnackbar('File is not supported at this time', {
              variant: 'error',
              autoHideDuration: null,
            });
            analytics.event(
              AnalyticsEvent.WM_NIGEL_FILE_UPLOAD_FAILED,
              fileUploadAnalyticsMetadata,
              {
                fileName: file.name,
                fileSize: file.size,
                fileType: file.type,
              }
            );
            throw Error(messageFile.statusText);
          } else {
            filesJustProcessed.push(messageFile);
          }
        } catch (error) {
          enqueueSnackbar('Failed to upload file', { variant: 'error' });
        }
      })();
    });

    // Wait for all file uploads to complete
    await Promise.all(uploadPromises);
    if (passedFilesToProcess === undefined) {
      setFilesToProcess([]);
    }

    // Add the newly uploaded files to the selected files without overriding the existing ones
    const newSelectedFiles = selectedFiles
      ? [...selectedFiles, ...filesJustProcessed]
      : filesJustProcessed;

    if (currentlySelectedFiles !== undefined) {
      setSelectedFiles(newSelectedFiles);
    }

    if (passedFilesToProcess) {
      setFilesToUpload && setFilesToUpload([]);
      handleConfirmDialog && handleConfirmDialog(newSelectedFiles);
    }

    setShouldRefetchFiles(true);
    setIsProcessing(false);
    setIsLoading(false);
  };

  const handlePaste = (event: ClipboardEvent) => {
    const clipboardItems = event.clipboardData?.items ?? [];

    const pastedFiles: File[] = [];

    if (isProcessing) {
      return;
    }

    let textPasted = false;

    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.kind === 'string' && item.type === 'text/plain') {
        textPasted = true;
      } else if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (textPasted) {
      return;
    }

    if (pastedFiles.length > 0) {
      event.preventDefault();
      if (!openDialog) {
        setFilesToUpload && setFilesToUpload(pastedFiles);
        processFiles(pastedFiles);
      } else {
        setFilesToProcess((prev) => [...prev, ...pastedFiles]);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('paste', handlePaste as EventListener);
    return () => {
      window.removeEventListener('paste', handlePaste as EventListener);
    };
  });

  useEffect(() => {
    if (openDialog) {
      setShouldRefetchFiles(true);
    }
  }, [openDialog, setShouldRefetchFiles]);

  useEffect(() => {
    setSelectedFiles(currentlySelectedFiles);
  }, [currentlySelectedFiles]);

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    analytics.event(AnalyticsEvent.WM_NIGEL_UPLOAD_DIALOG_UPLOAD_FILES, undefined, {
      numFiles: e.target.files?.length ?? 0,
    });
    if (!e.target.files || e.target.files.length === 0) {
      enqueueSnackbar('No files selected.', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    const files = Array.from(e.target.files);

    e.target.value = '';

    setFilesToProcess((prev) => [...prev, ...files]);
  };

  return (
    <Dialog open={openDialog} onClose={handleCloseDialog}>
      <DialogTitle>
        File Upload
        <Typography variant="caption1" sx={{ display: 'block', lineHeight: '18px' }}>
          <br />
          For the vision capable models, images (png, jpg, jpeg) will be analyzed by the model. For
          other models, text is extracted as a preprocessing step and sent to the model.
          <br />
          Text can be extracted from the following file types without issue: pdf, docx, pptx, xlsx,
          png, jpg, jpeg, text-based (txt, js, html, etc.). Success with other file types will vary.
        </Typography>
      </DialogTitle>
      <IconButton
        onClick={handleCloseDialog}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: '#757575',
        }}
      >
        <Close />
      </IconButton>
      <DialogContainer
        maxWidth="lg"
        sx={{ marginBottom: '0px ', minWidth: '270px', pl: '0px', pr: '0px' }}
      >
        <LoadingMask isLoading={isLoading} />

        <FilesSection>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginLeft: '15px',
              marginRight: '15px',
              alignItems: 'flex-start',
            }}
          >
            <div
              style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => {
                //Clicks the hidden input to open native file selection
                (document.getElementById('upload-files') as HTMLButtonElement).click();
              }}
            >
              <Typography variant="h6">Upload Files</Typography>
              <IconButton
                id="upload-files"
                component="label"
                style={{ left: '8px' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input type="file" hidden multiple onChange={handleFileUpload} />

                <Attachment />
              </IconButton>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column' }}>
              {currentFolder?.id !== myFolder.id && !folderFilesOnly && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption2" sx={{ textAlign: 'right' }}>
                    Upload Files to Folder
                  </Typography>
                  <Switch
                    checked={filesForFolder}
                    onChange={(event) => {
                      setFilesForFolder(event.target.checked);
                    }}
                  />
                </div>
              )}
              {selectedFiles && (
                <Button
                  variant="outlined"
                  onClick={() => processFiles()}
                  disabled={filesToProcess.length === 0}
                >
                  Process Uploaded Documents
                </Button>
              )}
            </div>
          </div>
          {filesToProcess.length > 0 ? (
            <div style={{ maxHeight: '275px', overflowY: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filesToProcess.map((file, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <AutoTruncatingTypographyWithTooltip text={file.name} variant="caption1" />
                      </TableCell>
                      <TableCell>
                        <RemoveCircleOutline
                          onClick={() => {
                            setFilesToProcess((prev) => prev.filter((_, i) => i !== index));
                          }}
                          sx={{ cursor: 'pointer' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <Typography
              variant="caption"
              sx={{ cursor: 'pointer', ml: '15px', mr: '15px' }}
              onClick={() => {
                (document.getElementById('upload-files') as HTMLButtonElement).click();
              }}
            >
              Paste or upload documents here
            </Typography>
          )}
        </FilesSection>
        {((selectedFiles && userFiles.length > 0) || searchQuery !== '') && (
          <div>
            <Tooltip title="Go to Files Page">
              <Typography
                onClick={() => {
                  navigate(`/folder/${currentFolder?.id}/files`);
                }}
                sx={{ cursor: 'pointer', width: 'fit-content', ml: '15px', mr: '15px' }}
                variant="h6"
              >
                Files
              </Typography>
            </Tooltip>
          </div>
        )}
        {showSearch && (searchQuery !== '' || userFiles.length > 0) && (
          <SearchContainer>
            <DebouncedSearchInput
              labelText="Search files by name"
              value={searchQuery}
              valueUpdated={(value) => {
                dispatch(setSearchQuery(value));
                dispatch(getUserFiles());
                if (currentFolder?.id) {
                  dispatch(getFolderFiles({ folderId: currentFolder.id }));
                }
              }}
            />
          </SearchContainer>
        )}
        {selectedFiles !== undefined && (userFiles.length > 0 || folderFiles.length > 0) ? (
          <FilesSection>
            <div style={{ maxHeight: '275px', overflowY: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>Filename</TableCell>
                    <TableCell sx={{ width: '140px' }}>Uploaded At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {folderFiles &&
                    folderFiles.map((file) => (
                      <TableRow
                        key={file.id}
                        sx={{
                          ...(selectedFiles.some((selectedFile) => selectedFile.id === file.id)
                            ? { backgroundColor: '#CED7E6' }
                            : {}),
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          setSelectedFiles((prev) => {
                            const prevFiles = prev as CustomFile[];
                            const foundFile = prevFiles.find((f) => f.id === file.id);
                            if (foundFile) {
                              return prevFiles.filter((f) => f.id !== file.id);
                            } else {
                              return [...prevFiles, file];
                            }
                          })
                        }
                      >
                        <TableCell>
                          <FolderOutlined />
                        </TableCell>
                        <TableCell sx={!file.pinned ? { pl: '16px' } : {}}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            {file.pinned && (
                              <PushPin sx={{ height: '12px', width: '12px', mr: '2px' }} />
                            )}
                            <AutoTruncatingTypographyWithTooltip
                              text={file.fileName}
                              variant="caption1"
                            />
                          </div>
                        </TableCell>
                        <TableCell sx={{ width: '140px' }}>{formatDate(file.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                  {userFiles.map((file) => (
                    <TableRow
                      key={file.id}
                      sx={{
                        ...(selectedFiles.some((selectedFile) => selectedFile.id === file.id)
                          ? { backgroundColor: '#CED7E6' }
                          : {}),
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectedFiles((prev) => {
                          const prevFiles = prev as CustomFile[];
                          const foundFile = prevFiles.find((f) => f.id === file.id);
                          if (foundFile) {
                            return prevFiles.filter((f) => f.id !== file.id);
                          } else {
                            return [...prevFiles, file];
                          }
                        })
                      }
                    >
                      <TableCell>
                        <PersonOutline />
                      </TableCell>
                      <TableCell sx={!file.pinned ? { pl: '16px' } : {}}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          {file.pinned && (
                            <PushPin sx={{ height: '12px', width: '12px', mr: '2px' }} />
                          )}
                          <AutoTruncatingTypographyWithTooltip
                            text={file.fileName}
                            variant="caption1"
                          />
                        </div>
                      </TableCell>
                      <TableCell sx={{ width: '140px' }}>{formatDate(file.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </FilesSection>
        ) : (
          showSearch &&
          searchQuery !== '' && (
            <Typography sx={{ textAlign: 'center' }}>
              No files found matching your search.
            </Typography>
          )
        )}
      </DialogContainer>
      <DialogActions>
        <Button variant="outlined" onClick={handleCloseDialog}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={
            isLoading ||
            (handleConfirmDialog && filesToProcess.length > 0) ||
            (!selectedFiles && filesToProcess.length === 0)
          }
          onClick={
            handleConfirmDialog && selectedFiles
              ? () => handleConfirmDialog(selectedFiles)
              : () => {
                  processFiles();
                  handleCloseDialog();
                }
          }
        >
          {buttonConfirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FileUploadDialog;
